import { Avatar, Box, Button, Typography, useMediaQuery } from "@mui/material";
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { DrawerItem, menuItems, kiteMenuItems, wingMenuItems, comunidadeMenuItems, kitesMenuItems, barrasMenuItems, pranchasMenuItems, wingPranchasMenuItems, trapezioMenuItems, wingsMenuItems, wingFoilMenuItems, timeMenuItems, centroMenuItems } from "./ItemsDrawer";

interface CascadeLevelDrawerProps {
    level: number,
    setLevel: React.Dispatch<React.SetStateAction<number>>,
    historico: DrawerItem[],
    setHistorico: React.Dispatch<React.SetStateAction<DrawerItem[] | []>>,
    handleDrawerState: () => void
}

const CascadeLevelDrawer: React.FC<CascadeLevelDrawerProps> = ({
    level,
    setLevel,
    historico,
    setHistorico,
    handleDrawerState
}) => {

    const { t } = useTranslation();
    const navigate = useNavigate();

    const changeDrawerOrientation = useMediaQuery('(max-width: 769px)');

    const onClickButton = useCallback((item: DrawerItem) => {
        if (!item.hasArrow) {
            if (item.label === "LOJA") {
                window.open(item.href, "_blank");
                return;
            } else {
                navigate(item.navigate || "")
                handleDrawerState();
            }
        } else {
            setLevel(item.level + 1);
        }

        setHistorico((prevLista) => {
            if (item.level === 0) {
                // Substitui o histórico por um novo array apenas com o item atual
                return [item];
            }

            if (item.level === 1) {
                // Verifica se existe um item na posição [1]
                if (prevLista[1]) {
                    // Se o item for diferente do existente na posição [1], substitui
                    if (prevLista[1].label !== item.label) {
                        const newList = [...prevLista];
                        newList[1] = item;
                        return newList;
                    }
                    // Se for o mesmo item, retorna o array original
                    return prevLista;
                }

                // Caso não exista item na posição [1], adiciona o novo item
                return [...prevLista, item];
            }

            // Adiciona o item para outros níveis (>= 2)
            return [...prevLista, item];
        });
    }, [setHistorico, setLevel])

    const listItems = useMemo(() => {
        if (level === 0) {
            return menuItems;
        } else if (level === 1) {
            switch (historico[0].label) {
                case "KITE":
                    return kiteMenuItems;
                case "WING":
                    return wingMenuItems;
                case "COMUNIDADE":
                    return comunidadeMenuItems;
                default:
                    return menuItems;
            }
        } else {
            switch (historico[1].label) {
                case "KITES":
                    return kitesMenuItems;
                case "BARRAS":
                    return barrasMenuItems;
                case "PRANCHAS":
                    if (historico[0].label === 'KITE') {
                        return pranchasMenuItems
                    } else {
                        return wingPranchasMenuItems;
                    }
                case "TRAPÉZIO":
                    return trapezioMenuItems;
                case "WINGS":
                    return wingsMenuItems;
                case "FOILS":
                    return wingFoilMenuItems;
                case "TIME":
                    return timeMenuItems;
                case "CENTROS":
                    return centroMenuItems;
                default:
                    return menuItems;
            }
        }
    }, [level, historico])

    const calculateWidth = useMemo(() => {
        if (level === 0) {
            return { xs: "100%", sm: changeDrawerOrientation ? "100%" : 210, md: 300, lg: 320, xl: 350 }
        } else if (level === 1) {
            return { xs: "100%", sm: changeDrawerOrientation ? "100%" : 180, md: 230, lg: 290, xl: 350 }
        } else {
            return 400
        }
    }, [level, changeDrawerOrientation])

    const showArrow = useCallback((item : DrawerItem) => {
        if(level === historico.length && item.hasArrow){
            return true
        }else if(level === 0 && item.hasArrow && historico.includes(item)){
            return true
        }else if(level === 1 && item.hasArrow && historico.includes(item)){
            return true
        }
        else if(level === 0 && item.hasArrow && historico.length === 0){
            return true
        }
        return false

    },[level, historico])

    const cascadeLevelDrawerContent = useCallback((item: DrawerItem) => {

        if (level < 2 || historico[0].label == "COMUNIDADE") {
            return (
                <Button
                    key={item.label + "" + item.level}
                    fullWidth
                    disableRipple
                    onClick={() => { onClickButton(item) }}
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "10px 20px",
                        color: "#fff",
                        fontSize: level === 0 ? { xs: 22, sm: 19, md: 24, lg: 26, xl: 28 } : { xs: 22, sm: changeDrawerOrientation ? 28 : 17, md: 21, lg: 24, xl: 25 },
                        fontWeight: "bold",
                        letterSpacing: "0.05em",
                        textTransform: "uppercase",
                        backgroundColor: "transparent",
                        borderRadius: 0,
                        "&:hover": {
                            backgroundColor: "inherit",
                        },
                    }}
                    endIcon={showArrow(item) && <ArrowForwardIosIcon sx={{ fontSize: "1rem", color: "#fff" }} />}
                >
                    {t(item.label)}
                </Button>
            )
        } else {
            return (
                <a
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        textDecoration: 'none',
                        color: 'inherit',
                        padding: '10px 0',
                    }}
                    key={item.label + "" + item.level}
                >
                    <Avatar
                        sx={{
                            backgroundColor: 'transparent',
                            width: '100px',
                            height: '100px',
                        }}
                    >
                        <img
                            src={item.src}
                            alt="Imagem de Exemplo"
                            style={{ width: '100%', height: '90%', objectFit: 'contain' }}
                        />
                    </Avatar>
                    <Box sx={{marginLeft: '16px'}}>
                        <Typography fontSize={'1.2rem'} sx={{ fontWeight: 'bold', color: "white" }} component="div">
                            {t(item.label)}
                        </Typography>
                        <Typography fontSize={'0.85rem'} sx={{ fontWeight: 'bold', color: 'white' }}>
                            {t(item.secondary || "")}
                        </Typography>
                    </Box>
                </a>
            )
        }
    }, [t,changeDrawerOrientation,level,historico])

    return (
        <Box
            sx={{
                backgroundColor: "#000",
                color: "#fff",
                width: calculateWidth,
            }}
        >
            {listItems.map((item, _index) => (cascadeLevelDrawerContent(item)))}
        </Box>
    );
}

export default CascadeLevelDrawer